<script setup lang="ts">
import { computed, onMounted, onUnmounted, Ref, ref } from 'vue'

import { nullCoalesce, opChain } from '@collector/shared-utils'
import { EventScoutRank } from '@collector/sportsapi-client-legacy'
import ProtocolHeaderButton from '@mobile/components/HeaderButton/ProtocolHeaderButton.vue'
import TrackerHeaderButton from '@mobile/components/HeaderButton/TrackerHeaderButton.vue'
import { useSportOptions } from '@mobile/views/Relation/DetailsPanel/Sports/composables/useSportOptions'
import { useEventRelationSettings } from '@mobile/views/Relation/DetailsPanel/Sports/useEventRelationSettings'
import PinnedIncidentsTable from '@mobile/views/Relation/DetailsPanel/Table/PinnedIncidentsTable.vue'
import { useHistoryIncidents } from '@mobile/views/Relation/DetailsPanel/Table/useHistoryIncidents'

import EventHeaderNotificationsButton from '../Shared/EventHeader/Buttons/EventHeaderNotificationsButton.vue'
import EventHeaderQueueButton from '../Shared/EventHeader/Buttons/EventHeaderQueueButton.vue'
import { HeaderPopupKey } from '../Shared/EventHeader/HeaderPopupKey'
import NotificationsView from '../Shared/Popups/Notifications/NotificationsView.vue'
import Queue from '../Shared/Popups/Queue/Queue.vue'
import QueueBlockedOverlay from '../Shared/Popups/Queue/QueueBlockedOverlay.vue'
import { useEventInjections } from '../Shared/RelationDependencies/useEventInjections'
import DetailsPanelSettings from './components/Settings/DetailsPanelSettings/DetailsPanelSettings.vue'
import PeriodSummary from './components/Summary/PeriodSummary.vue'
import Summary from './components/Summary/Summary.vue'
import { useIncidentModals } from './composables/useIncidentModals'
import { useModalsHost } from './composables/useModals'
import PocketIncidents from './PocketIncidents/PocketIncidents.vue'
import HistoryTable from './Table/HistoryTable.vue'

const eventInjections = useEventInjections()
const {
  probableHomeParticipant,
  probableAwayParticipant,
  eventsScouts,
  sport,
} = eventInjections
const sportOptionsComposable = useSportOptions(sport)
const { showCurrentPeriodResult, reversePagination } = sportOptionsComposable
const { load: loadSettings, initialized: settingsInitialized } =
  useEventRelationSettings(eventInjections, sportOptionsComposable)
const currentPopupOpened: Ref<HeaderPopupKey | null> = ref(null)
const firstScout = computed(() =>
  eventsScouts.value.find((scout) => scout.rank === EventScoutRank.Main),
)
const secondScout = computed(() =>
  eventsScouts.value.find((scout) => scout.rank === EventScoutRank.Helper),
)
const { pinnedIncidentIds, selectedIncident } =
  useHistoryIncidents(eventInjections)

const scrollY = ref(0)

const { currentModal } = useIncidentModals()

useModalsHost()

function openPopup(key: HeaderPopupKey): void {
  if (currentPopupOpened.value === key) {
    currentPopupOpened.value = null

    return
  }

  currentPopupOpened.value = key
}

loadSettings()

function updateScroll(): void {
  scrollY.value = window.scrollY
}

onMounted(() => {
  document.addEventListener('scroll', updateScroll)
})

onUnmounted(() => {
  document.removeEventListener('scroll', updateScroll)
})
</script>

<template>
  <div>
    <div
      v-if="selectedIncident"
      class="bg-silver/75 fixed inset-0 z-70"
      data-testid="undock-incident-button"
    ></div>
    <div
      v-if="settingsInitialized"
      class="relative"
    >
      <div
        class="bg-silver sticky inset-0 z-60"
        :class="{ 'shadow-md': scrollY > 0 }"
      >
        <div class="flex justify-between py-4">
          <div class="space-y-4">
            <div class="flex items-center space-x-4 px-4">
              <div>
                <span class="font-bold">
                  {{ probableHomeParticipant.short_name }}
                </span>
                -
                <span class="font-bold">
                  {{ probableAwayParticipant.short_name }}
                </span>
              </div>
            </div>
            <div class="flex space-x-6 px-4 text-sm">
              <div class="space-x-1">
                <span class="text-cloudygray">First Scout:</span>
                <span class="sentry-mask font-bold">
                  {{
                    nullCoalesce(
                      opChain(firstScout, (v) => v.full_name),
                      '---',
                    )
                  }}
                </span>
              </div>
              <div class="space-x-1">
                <span class="text-cloudygray">Second Scout:</span>
                <span class="sentry-mask font-bold">
                  {{
                    nullCoalesce(
                      opChain(secondScout, (v) => v.full_name),
                      '---',
                    )
                  }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex px-4">
            <TrackerHeaderButton />
            <ProtocolHeaderButton />
            <div class="relative inline-block">
              <EventHeaderNotificationsButton @click="openPopup" />
              <div
                v-if="currentPopupOpened === HeaderPopupKey.Notifications"
                class="absolute inset-0 h-full"
                @click="currentPopupOpened = null"
              >
                <div
                  class="absolute top-10 right-0 z-20 bg-white shadow-md"
                  style="width: 700px"
                  @click.stop=""
                >
                  <NotificationsView class="shadow-md" />
                </div>
              </div>
            </div>
            <div class="relative inline-block">
              <EventHeaderQueueButton @click="openPopup" />
              <div
                v-if="currentPopupOpened === HeaderPopupKey.Queue"
                class="absolute inset-0 h-full"
                @click="currentPopupOpened = null"
              >
                <div
                  class="absolute top-10 right-0 z-20 bg-white shadow-md"
                  style="width: 700px"
                  @click.stop=""
                >
                  <Queue />
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr class="text-lightgray" />
      </div>
      <div class="pb-4">
        <PeriodSummary v-if="showCurrentPeriodResult" />
        <Summary v-else />
      </div>
      <hr class="text-lightgray mx-4 mb-4" />
      <DetailsPanelSettings />
      <PocketIncidents class="pb-6" />
      <div class="relative space-y-4 px-4">
        <QueueBlockedOverlay />
        <div class="max-w-screen overflow-x-scroll">
          <PinnedIncidentsTable v-if="pinnedIncidentIds.length" />
        </div>
        <div class="max-w-screen overflow-x-scroll">
          <HistoryTable :reversePagination />
        </div>
      </div>
      <component
        :is="currentModal"
        v-if="currentModal"
      />
    </div>
  </div>
</template>
